import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Download, Trash2, RefreshCw, CheckCircle, AlertTriangle, FileText, X, Loader2, Info, Upload, FileCheck2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * StatementVaultDrawer - slide-over panel listing all stored statements for a
 * single bank account. Supports download, reconcile-again, and delete (only
 * for user-uploaded files; auto-fetched Mono ones are protected).
 *
 * Props:
 *   open          - boolean
 *   onClose()     - called on close
 *   bankAccount   - row from /api/bank-accounts (must include id + display fields)
 */
export default function StatementVaultDrawer({ open, onClose, bankAccount }) {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ period_start: '', period_end: '', statement_type: 'monthly', file: null });

  const SOURCE_LABEL = {
    manual_upload: 'Uploaded for records',
    bank_feed_upload: 'Imported via statement upload (bank feed)',
    mono_auto: 'Fetched from your bank',
    plaid_auto: 'Fetched from your bank',
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!form.file || !form.period_start || !form.period_end) {
      toast.error('Choose a PDF and the statement period');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('bank_account_id', bankAccount.id);
      fd.append('period_start', form.period_start);
      fd.append('period_end', form.period_end);
      fd.append('statement_type', form.statement_type);
      fd.append('file', form.file);
      const { data } = await axios.post(`${API}/api/statement-vault/upload`, fd);
      toast.success(data.duplicate ? 'This statement was already on file' : 'Statement saved for your records');
      setShowUpload(false);
      setForm({ period_start: '', period_end: '', statement_type: 'monthly', file: null });
      fetchList();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error((d && (d.message || d)) || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const fetchList = React.useCallback(async () => {
    if (!bankAccount?.id) return;
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/statement-vault`,
        { params: { bank_account_id: bankAccount.id } }
      );
      setStatements(data.statements || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load statements');
    } finally {
      setLoading(false);
    }
  }, [bankAccount?.id]);

  useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  if (!open) return null;

  const currency = bankAccount.currency || 'GBP';
  const fmt = (n) => (n == null ? '-' : n.toLocaleString(undefined, { style: 'currency', currency }));

  const handleDownload = async (s) => {
    setBusyId(s.id);
    try {
      const resp = await axios.get(`${API}/api/statement-vault/${s.id}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = s.original_filename || `statement_${s.period_label}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Download failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleReconcile = async (s) => {
    setBusyId(s.id);
    try {
      await axios.post(`${API}/api/statement-vault/${s.id}/reconcile`);
      toast.success('Reconciled');
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reconcile failed');
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (s) => {
    if (!window.confirm(`Delete statement for ${s.period_label}? This cannot be undone.`)) return;
    setBusyId(s.id);
    try {
      await axios.delete(`${API}/api/statement-vault/${s.id}`);
      toast.success('Statement deleted');
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50" data-testid="statement-vault-drawer">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-xl bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Statement Vault</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {bankAccount.institution_name || bankAccount.bank_name} ****{(bankAccount.mask || bankAccount.account_number || '').slice(-4)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowUpload((v) => !v)}
              data-testid="statement-vault-upload-toggle"
              className="text-xs font-medium text-[#108981] border border-[#108981]/40 hover:bg-emerald-50 rounded-md px-2.5 py-1 flex items-center gap-1.5"
            >
              <Upload className="w-3.5 h-3.5" /> Upload for records
            </button>
            <button
              onClick={onClose}
              data-testid="statement-vault-close"
              className="p-1 text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {showUpload && (
          <form onSubmit={handleUpload} className="p-4 border-b border-slate-200 bg-slate-50 space-y-3" data-testid="statement-vault-upload-form">
            <p className="text-xs text-slate-600">
              Keeps the PDF on file for this account (record-keeping only — nothing is imported into your books). To add transactions, use <strong>Upload Statement</strong> on the account tile.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-slate-600">From
                <input type="date" required value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                  className="mt-1 w-full border border-slate-300 rounded px-2 py-1 text-sm" data-testid="statement-vault-period-start" />
              </label>
              <label className="text-xs text-slate-600">To
                <input type="date" required value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                  className="mt-1 w-full border border-slate-300 rounded px-2 py-1 text-sm" data-testid="statement-vault-period-end" />
              </label>
            </div>
            <div className="flex items-center gap-2">
              <select value={form.statement_type} onChange={(e) => setForm({ ...form, statement_type: e.target.value })}
                className="border border-slate-300 rounded px-2 py-1 text-sm" data-testid="statement-vault-type">
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
              </select>
              <input type="file" accept="application/pdf,.pdf" onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                className="text-xs flex-1" data-testid="statement-vault-file" />
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setShowUpload(false)} className="text-xs px-2.5 py-1 rounded border border-slate-300 text-slate-600">Cancel</button>
              <button type="submit" disabled={uploading} data-testid="statement-vault-upload-submit"
                className="text-xs px-3 py-1 rounded bg-[#108981] text-white font-medium disabled:opacity-60 flex items-center gap-1.5">
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Save statement
              </button>
            </div>
          </form>
        )}

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : statements.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-500">No statements yet.</p>
              <p className="text-xs text-slate-400 mt-1">Statements you import on the account tile appear here automatically. Use “Upload for records” to keep a PDF on file without importing it.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {statements.map((s) => (
                <div
                  key={s.id}
                  className="border border-slate-200 rounded-md p-3 bg-white"
                  data-testid={`statement-row-${s.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-slate-400" />
                        {s.period_label}
                        {s.variance_status === 'ok' && (
                          <span className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Reconciled
                          </span>
                        )}
                        {s.variance_status === 'flagged' && (
                          <span className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> Variance
                          </span>
                        )}
                        {s.variance_status === 'imported' && (
                          <span className="text-xs text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5 flex items-center gap-1" data-testid={`statement-imported-${s.id}`}>
                            <FileCheck2 className="w-3 h-3" /> Imported{s.transaction_count != null ? ` · ${s.transaction_count} transactions` : ''}
                          </span>
                        )}
                        {s.variance_status === 'unchecked' && (
                          <span className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 flex items-center gap-1">
                            <Info className="w-3 h-3" /> Unchecked
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {s.period_start} → {s.period_end}
                        {s.original_filename && <> · {s.original_filename}</>}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-0.5" data-testid={`statement-source-${s.id}`}>
                        {SOURCE_LABEL[s.source] || s.source}{s.uploaded_at ? ` · ${String(s.uploaded_at).slice(0, 10)}` : ''}
                      </div>
                      {s.variance_status !== 'imported' && (
                      <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                        <div>
                          <div className="text-slate-400">Closing (PDF)</div>
                          <div className="font-medium">{fmt(s.closing_balance_extracted)}</div>
                        </div>
                        <div>
                          <div className="text-slate-400">Closing (Ledger)</div>
                          <div className="font-medium">{fmt(s.ledger_closing_balance)}</div>
                        </div>
                        <div>
                          <div className="text-slate-400">Variance</div>
                          <div className={`font-medium ${s.variance_status === 'flagged' ? 'text-rose-600' : 'text-slate-700'}`}>{fmt(s.variance)}</div>
                        </div>
                      </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-1 mt-3 pt-2 border-t border-slate-100">
                    <button
                      data-testid={`statement-download-${s.id}`}
                      onClick={() => handleDownload(s)}
                      disabled={busyId === s.id}
                      className="px-2 py-1 text-xs text-slate-700 hover:bg-slate-100 rounded flex items-center gap-1.5"
                    >
                      <Download className="w-3.5 h-3.5" /> Download
                    </button>
                    {s.closing_balance_extracted != null && (
                      <button
                        data-testid={`statement-reconcile-${s.id}`}
                        onClick={() => handleReconcile(s)}
                        disabled={busyId === s.id}
                        className="px-2 py-1 text-xs text-slate-700 hover:bg-slate-100 rounded flex items-center gap-1.5"
                      >
                        <RefreshCw className={`w-3.5 h-3.5 ${busyId === s.id ? 'animate-spin' : ''}`} /> Re-reconcile
                      </button>
                    )}
                    {s.source === 'manual_upload' && (
                      <button
                        data-testid={`statement-delete-${s.id}`}
                        onClick={() => handleDelete(s)}
                        disabled={busyId === s.id}
                        className="px-2 py-1 text-xs text-rose-700 hover:bg-rose-50 rounded flex items-center gap-1.5"
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
